import React from "react";
import { Box, Button, Card, IconButton, Typography } from "@material-ui/core";
import moment from "moment";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import EventBusyRoundedIcon from "@material-ui/icons/EventBusyRounded";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import {
  FOLLOWUP_STATUS,
  FOLLOWUP_STATUS_ORDER,
  mobileOf,
  patientNameOf,
  statusMeta,
} from "../../followUpHelpers";
import { useDaySidebarStyles } from "./styles";

/**
 * The follow-ups of the day selected on the calendar, grouped by status. Every
 * action available on a table row (reschedule / schedule appointment / view
 * appointment / close) is reachable here through the same menu the table uses.
 */
const FollowUpDaySidebar = ({
  selectedDate,
  followUps = [],
  onCaseClick,
  onReschedule,
  onMenuClick,
}) => {
  const classes = useDaySidebarStyles();

  // Known statuses first, then any status the backend may add later so a
  // follow-up can never silently disappear from the day view.
  const sectionStatuses = [
    ...FOLLOWUP_STATUS_ORDER,
    ...followUps
      .map((fu) => fu.status)
      .filter((s) => s && !FOLLOWUP_STATUS_ORDER.includes(s)),
  ].filter((s, i, all) => all.indexOf(s) === i);

  const renderCard = (fu) => {
    const meta = statusMeta(fu.status);
    const appointmentDate =
      fu.status === FOLLOWUP_STATUS.SCHEDULED && fu.appointmentDate
        ? moment(fu.appointmentDate).format("DD MMM YYYY")
        : null;

    return (
      <Box
        key={fu.id}
        className={classes.fuCard}
        style={{ borderLeftColor: meta.dot }}
      >
        <Box className={classes.fuCardTop}>
          <Typography className={classes.fuName}>
            {patientNameOf(fu)}
          </Typography>
          <Typography
            className={classes.caseLink}
            onClick={() => onCaseClick(fu.caseId)}
          >
            #{fu.caseId}
          </Typography>
        </Box>

        <Typography className={classes.fuMeta}>{mobileOf(fu)}</Typography>

        {appointmentDate && (
          <Typography className={classes.fuMeta}>
            Appointment: {appointmentDate}
          </Typography>
        )}

        <Box className={classes.fuActionRow}>
          {fu.action ? (
            <Typography className={classes.actionTaken}>
              {fu.action}
            </Typography>
          ) : (
            <Typography className={classes.actionTaken}>No action yet</Typography>
          )}

          {fu.status !== FOLLOWUP_STATUS.CLOSED && (
            <Box display="flex" alignItems="center">
              <Button
                size="small"
                variant="outlined"
                color="primary"
                className={classes.rescheduleBtn}
                onClick={() => onReschedule(fu)}
              >
                Reschedule
              </Button>
              <IconButton size="small" onClick={(e) => onMenuClick(e, fu)}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Card className={classes.sidebarCard} elevation={0}>
      <Box className={classes.sidebarHeader}>
        <CalendarTodayIcon className={classes.headerIcon} fontSize="small" />
        <Typography className={classes.sidebarTitle}>
          {selectedDate.format("ddd, DD MMM YYYY")}
        </Typography>
      </Box>
      <Typography className={classes.sidebarSubtitle}>
        {followUps.length}
        {followUps.length === 1 ? " follow-up" : " follow-ups"}
      </Typography>

      {followUps.length === 0 ? (
        <Box className={classes.emptyState}>
          <EventBusyRoundedIcon className={classes.emptyIcon} />
          <Typography className={classes.emptyText}>
            No follow-ups on this day
          </Typography>
        </Box>
      ) : (
        sectionStatuses.map((status) => {
          const group = followUps.filter((fu) => fu.status === status);
          if (group.length === 0) return null;
          const meta = statusMeta(status);
          return (
            <Box key={status} className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Box
                  className={classes.sectionDot}
                  style={{ backgroundColor: meta.dot }}
                />
                <Typography
                  className={classes.sectionLabel}
                  style={{ color: meta.color }}
                >
                  {meta.label} ({group.length})
                </Typography>
              </Box>
              {group.map(renderCard)}
            </Box>
          );
        })
      )}
    </Card>
  );
};

export default FollowUpDaySidebar;
